<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowLeft, Check, X } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { useProcurementApi } from '../composables/useProcurementApi'
import AgentPickerModal from '../components/AgentPickerModal.vue'
import type {
  AgentOption,
  CurrencyOption,
  PurchaseOrder,
  SaveSupplierInvoicePayload,
  SupplierInvoice,
} from '../types/procurement'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const api = useProcurementApi()

const editId = computed(() => (route.params.id ? Number(route.params.id) : null))
const isEdit = computed(() => editId.value !== null)

const currencies = ref<CurrencyOption[]>([])
const loading = ref(true)
const error = ref('')
const saving = ref(false)
const saveError = ref('')

const showSupplierPicker = ref(false)
const showWarehousePicker = ref(false)
const showDepartmentPicker = ref(false)

const supplierName = ref('')
const warehouseName = ref('')
const departmentName = ref('')

const orders = ref<PurchaseOrder[]>([])
const currentStatus = ref('')
const voidInvoice = ref(false)

const form = reactive({
  supplier_agent_id: '',
  purchase_order_id: '',
  invoice_number: '',
  invoice_date: '',
  due_date: '',
  currency_code: '',
  exchange_rate: 1 as number | string,
  invoice_amount: '' as number | string,
  notes: '',
  warehouse_agent_id: '',
  department_agent_id: '',
})

const today = new Date()

function isoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** Purchase orders of the chosen supplier that share the invoice currency. */
const matchingOrders = computed(() =>
  orders.value.filter((order) => !form.currency_code || order.currency_code === form.currency_code),
)

const canVoid = computed(() => isEdit.value && currentStatus.value !== '' && currentStatus.value !== 'void')

function money(value: number, currency: string | null | undefined): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  return currency ? `${formatted} ${currency}` : formatted
}

async function loadOrders() {
  const supplierId = Number(form.supplier_agent_id || 0)
  if (!supplierId) {
    orders.value = []
    return
  }

  try {
    const result = await api.listPurchaseOrders({ supplier_agent_id: supplierId, per_page: 100 })
    orders.value = result.items
  } catch {
    orders.value = []
  }
}

watch(() => form.supplier_agent_id, () => {
  // A different supplier invalidates any linked order.
  form.purchase_order_id = ''
  void loadOrders()
})

function pickSupplier(agent: AgentOption) {
  form.supplier_agent_id = String(agent.id)
  supplierName.value = agent.name
  showSupplierPicker.value = false
}

function pickWarehouse(agent: AgentOption) {
  form.warehouse_agent_id = String(agent.id)
  warehouseName.value = agent.name
  showWarehousePicker.value = false
}

function pickDepartment(agent: AgentOption) {
  form.department_agent_id = String(agent.id)
  departmentName.value = agent.name
  showDepartmentPicker.value = false
}

async function loadInvoice() {
  if (!editId.value) return

  const invoice: SupplierInvoice = await api.getSupplierInvoice(editId.value)
  form.supplier_agent_id = String(invoice.supplier_agent_id)
  supplierName.value = invoice.supplier_name ?? `#${invoice.supplier_agent_id}`
  form.purchase_order_id = invoice.purchase_order_id ? String(invoice.purchase_order_id) : ''
  form.invoice_number = invoice.invoice_number
  form.invoice_date = (invoice.invoice_date ?? '').slice(0, 10)
  form.due_date = (invoice.due_date ?? '').slice(0, 10)
  form.currency_code = invoice.currency_code ?? ''
  form.exchange_rate = invoice.exchange_rate
  form.invoice_amount = invoice.invoice_amount
  form.notes = invoice.notes ?? ''
  form.warehouse_agent_id = invoice.warehouse_agent_id ? String(invoice.warehouse_agent_id) : ''
  warehouseName.value = invoice.warehouse_name
    ?? (invoice.warehouse_agent_id ? `#${invoice.warehouse_agent_id}` : '')
  form.department_agent_id = invoice.department_agent_id ? String(invoice.department_agent_id) : ''
  departmentName.value = invoice.department_name
    ?? (invoice.department_agent_id ? `#${invoice.department_agent_id}` : '')
  currentStatus.value = String(invoice.status)

  await loadOrders()
}

function validate(): string {
  if (!form.supplier_agent_id) return t('procurement.invoiceForm.validationSupplier')
  if (!form.currency_code) return t('procurement.invoiceForm.validationCurrency')
  if (!form.invoice_number.trim()) return t('procurement.invoiceForm.validationNumber')
  if (!form.invoice_date) return t('procurement.invoiceForm.validationInvoiceDate')
  if (!form.due_date || form.due_date < form.invoice_date) return t('procurement.invoiceForm.validationDueDate')
  if (Number(form.invoice_amount) <= 0) return t('procurement.invoiceForm.validationAmount')

  return ''
}

async function submit() {
  saveError.value = validate()
  if (saveError.value) return

  const payload: SaveSupplierInvoicePayload = {
    supplier_agent_id: Number(form.supplier_agent_id),
    purchase_order_id: form.purchase_order_id ? Number(form.purchase_order_id) : null,
    invoice_number: form.invoice_number.trim(),
    invoice_date: form.invoice_date,
    due_date: form.due_date,
    currency_code: form.currency_code,
    exchange_rate: Number(form.exchange_rate) || 1,
    invoice_amount: Number(form.invoice_amount),
    department_agent_id: form.department_agent_id ? Number(form.department_agent_id) : null,
    warehouse_agent_id: form.warehouse_agent_id ? Number(form.warehouse_agent_id) : null,
    notes: form.notes.trim() || null,
  }

  // Only `void` may be set directly — other statuses are allocation-derived.
  if (isEdit.value && voidInvoice.value) payload.status = 'void'

  saving.value = true
  try {
    const invoice = isEdit.value && editId.value
      ? await api.updateSupplierInvoice(editId.value, payload)
      : await api.createSupplierInvoice(payload)

    await router.push(`/supplier-invoices/${invoice.id}`)
  } catch (cause) {
    saveError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  loading.value = true
  error.value = ''
  try {
    const currencyList = await api.listCurrencies().catch(() => [] as CurrencyOption[])
    currencies.value = currencyList

    await loadInvoice()

    if (!isEdit.value) {
      form.invoice_date = isoDay(today)
      const due = new Date(today.getTime() + 30 * 86_400_000)
      form.due_date = isoDay(due)
      if (currencyList.length > 0 && !form.currency_code) form.currency_code = currencyList[0].code
    }
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="page-heading">
    <div>
      <RouterLink class="back" to="/supplier-invoices">
        <ArrowLeft :size="14" aria-hidden="true" />
        {{ t('procurement.invoiceForm.back') }}
      </RouterLink>
      <h2>{{ isEdit ? t('procurement.invoiceForm.editTitle', { id: editId }) : t('procurement.invoiceForm.createTitle') }}</h2>
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <RouterLink class="btn-primary" to="/supplier-invoices">
      <ArrowLeft :size="15" aria-hidden="true" />
      {{ t('procurement.invoiceForm.back') }}
    </RouterLink>
  </div>

  <form v-else class="invoice-form" @submit.prevent="submit">
    <div class="invoice-form__grid">
      <section class="panel invoice-form__section">
        <h3>{{ t('procurement.invoiceForm.heading') }}</h3>
        <p class="form-note">{{ t('procurement.invoiceForm.description') }}</p>

        <div class="field">
          <label>{{ t('procurement.invoiceForm.supplier') }}</label>
          <button class="field-button" type="button" @click="showSupplierPicker = true">
            <span>{{ supplierName || t('procurement.invoiceForm.selectSupplier') }}</span>
            <span aria-hidden="true">▾</span>
          </button>
        </div>

        <div class="field-inline">
          <div class="field">
            <label>{{ t('procurement.invoiceForm.invoiceNumber') }}</label>
            <input v-model="form.invoice_number" type="text" required :placeholder="t('procurement.invoiceForm.invoiceNumberPlaceholder')">
          </div>
          <div class="field">
            <label>{{ t('procurement.invoiceForm.amount') }}</label>
            <input v-model="form.invoice_amount" type="number" min="0.01" step="0.01" required>
          </div>
        </div>

        <div class="field-inline">
          <div class="field">
            <label>{{ t('procurement.invoiceForm.currency') }}</label>
            <select v-model="form.currency_code" required>
              <option value="">{{ t('procurement.common.selectPlaceholder') }}</option>
              <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
                {{ currency.code }}<template v-if="currency.name"> · {{ currency.name }}</template>
              </option>
            </select>
          </div>
          <div class="field">
            <label>{{ t('procurement.invoiceForm.exchangeRate') }}</label>
            <input v-model="form.exchange_rate" type="number" min="0.00000001" step="0.00000001">
          </div>
        </div>
      </section>

      <section class="panel invoice-form__section">
        <h3>{{ t('procurement.invoiceForm.purchaseOrder') }}</h3>

        <div class="field">
          <label>{{ t('procurement.invoiceForm.purchaseOrder') }} <small>{{ t('procurement.common.optional') }}</small></label>
          <select v-model="form.purchase_order_id" :disabled="!form.supplier_agent_id">
            <option value="">{{ t('procurement.invoiceForm.noOrders') }}</option>
            <option v-for="order in matchingOrders" :key="order.id" :value="String(order.id)">
              #{{ order.id }} · {{ order.supplier_name || '—' }} · {{ money(order.total_amount, order.currency_code) }} · {{ t(`procurement.status.${order.status}`) }}
            </option>
          </select>
          <small>{{ t('procurement.invoiceForm.purchaseOrderHint') }}</small>
        </div>

        <div class="field-inline">
          <div class="field">
            <label>{{ t('procurement.invoiceForm.invoiceDate') }}</label>
            <input v-model="form.invoice_date" type="date" required>
          </div>
          <div class="field">
            <label>{{ t('procurement.invoiceForm.dueDate') }}</label>
            <input v-model="form.due_date" type="date" :min="form.invoice_date || undefined" required>
          </div>
        </div>

        <div class="field">
          <label>{{ t('procurement.invoiceForm.warehouse') }} <small>{{ t('procurement.common.optional') }}</small></label>
          <button class="field-button" type="button" @click="showWarehousePicker = true">
            <span>{{ warehouseName || t('procurement.invoiceForm.selectWarehouse') }}</span>
            <span aria-hidden="true">▾</span>
          </button>
          <small v-if="form.warehouse_agent_id">
            <button
              class="link-action"
              type="button"
              @click="form.warehouse_agent_id = ''; warehouseName = ''"
            >
              <X :size="12" aria-hidden="true" />
              {{ t('procurement.common.clear') }}
            </button>
          </small>
        </div>

        <div class="field">
          <label>{{ t('procurement.invoiceForm.department') }} <small>{{ t('procurement.common.optional') }}</small></label>
          <button class="field-button" type="button" @click="showDepartmentPicker = true">
            <span>{{ departmentName || t('procurement.invoiceForm.selectDepartment') }}</span>
            <span aria-hidden="true">▾</span>
          </button>
          <small v-if="form.department_agent_id">
            <button
              class="link-action"
              type="button"
              @click="form.department_agent_id = ''; departmentName = ''"
            >
              <X :size="12" aria-hidden="true" />
              {{ t('procurement.common.clear') }}
            </button>
          </small>
        </div>

        <div class="field">
          <label>{{ t('procurement.invoiceForm.notes') }}</label>
          <textarea v-model="form.notes" rows="3" :placeholder="t('procurement.invoiceForm.notesPlaceholder')" />
        </div>

        <template v-if="canVoid">
          <label class="check-field">
            <input v-model="voidInvoice" type="checkbox">
            <span>{{ t('procurement.invoice.void') }}</span>
          </label>
          <p class="form-note">{{ t('procurement.invoiceForm.voidHint') }}</p>
        </template>
        <p v-else-if="!isEdit" class="form-note">{{ t('procurement.invoiceForm.statusHint') }}</p>
      </section>
    </div>

    <p v-if="saveError" class="error-inline">{{ saveError }}</p>

    <div class="form-actions">
      <button class="btn-primary" type="submit" :disabled="saving">
        <Check :size="15" aria-hidden="true" />
        {{ saving ? t('procurement.common.saving') : (isEdit ? t('procurement.invoiceForm.update') : t('procurement.invoiceForm.create')) }}
      </button>
      <RouterLink class="btn-secondary" to="/supplier-invoices">
        <X :size="15" aria-hidden="true" />
        {{ t('procurement.common.cancel') }}
      </RouterLink>
    </div>
  </form>

  <AgentPickerModal
    :open="showSupplierPicker"
    role="supplier"
    :title="t('procurement.invoiceForm.selectSupplier')"
    :selected-id="form.supplier_agent_id ? Number(form.supplier_agent_id) : null"
    @close="showSupplierPicker = false"
    @select="pickSupplier"
  />
  <AgentPickerModal
    :open="showWarehousePicker"
    role="warehouse"
    :title="t('procurement.invoiceForm.selectWarehouse')"
    :selected-id="form.warehouse_agent_id ? Number(form.warehouse_agent_id) : null"
    @close="showWarehousePicker = false"
    @select="pickWarehouse"
  />
  <AgentPickerModal
    :open="showDepartmentPicker"
    role="department"
    :title="t('procurement.invoiceForm.selectDepartment')"
    :selected-id="form.department_agent_id ? Number(form.department_agent_id) : null"
    @close="showDepartmentPicker = false"
    @select="pickDepartment"
  />
</template>

<style scoped>
.invoice-form { display: grid; gap: 16px; }
.invoice-form__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; align-items: start; }
.invoice-form__section { display: grid; gap: 12px; align-content: start; min-width: 0; }
.invoice-form__section > h3 { margin: 0; }
.invoice-form__section .field { min-width: 0; }
.invoice-form__section .field input,
.invoice-form__section .field select,
.invoice-form__section .field textarea,
.invoice-form__section .field-button { width: 100%; max-width: 100%; min-width: 0; }
.field-inline { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; min-width: 0; }

@media (max-width: 1024px) {
  .invoice-form__grid { grid-template-columns: minmax(0, 1fr); }
}

@media (max-width: 560px) {
  .field-inline { grid-template-columns: minmax(0, 1fr); }
}
</style>
